/**
 * The host's "Add people" sheet (ADR-040 Q12) — opened when an app calls the
 * `workspace-add-people` client-side entity (`useFiasWorkspaces().addPeople`).
 *
 * The PERSON chooses who joins one of the app's workspaces: co-members of the
 * Fias workspace they are acting in, or an exact username they type (there is
 * no search of strangers). Each add is this page's session call to the
 * session-only Add people route, which checks the person may add members
 * before it looks the username up. The app supplied only the workspace and a
 * suggested role, and learns only who was added, by its own ref for them.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api, type AppWorkspaceAddRole } from '@/lib/api';
import { useWorkspaceMembers } from '@/auth/hooks/useWorkspaceTeam';
import { useAuth } from '@/auth/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';
import type { AddedPerson, WorkspaceAddPeopleResult } from '@fias/client-entities';

interface AddPeopleSheetProps {
  open: boolean;
  /** The app the person is using: a real arche, or a preview's tenant. */
  archeId: string;
  /** The Fias workspace the person is acting in; null in their Personal one. */
  holderId: string | null;
  workspaceId: string;
  suggestedRole: AppWorkspaceAddRole;
  archeName: string;
  onResolve: (result: WorkspaceAddPeopleResult) => void;
}

const ROLE_LABELS: Record<AppWorkspaceAddRole, string> = {
  admin: 'Admin — manages members and edits',
  member: 'Member — edits',
  viewer: 'Viewer — reads only',
};

function describeAddError(err: unknown): string {
  const code = err instanceof ApiError ? err.code : undefined;
  switch (code) {
    case 'USER_NOT_FOUND':
      return 'No one has that username.';
    case 'ALREADY_MEMBER':
      return 'Already in this workspace.';
    case 'ROLE_CEILING':
      return 'You can’t give a role above your own.';
    case 'MEMBER_NOT_FOUND':
    case 'NOT_A_MEMBER':
    case 'FORBIDDEN':
      return 'You can’t add people to this workspace.';
    case 'MEMBER_LIMIT_REACHED':
      return 'This workspace is full.';
    case 'RATE_LIMIT':
      return 'Too many at once. Try again in a moment.';
    default:
      return 'This person could not be added.';
  }
}

export function AddPeopleSheet({
  open,
  archeId,
  holderId,
  workspaceId,
  suggestedRole,
  archeName,
  onResolve,
}: AddPeopleSheetProps) {
  const members = useWorkspaceMembers(holderId);
  // Which of the app's workspaces people are going into. Its name is the
  // app's own text, so it is quoted and never stands in for the app's name.
  const target = useQuery({
    queryKey: queryKeys.vault.appWorkspaceForAddPeople(archeId, workspaceId),
    queryFn: () => api.getAppWorkspaceForAddPeople(archeId, workspaceId),
    enabled: open,
    retry: false,
  });
  const workspaceName = target.data?.workspace.displayName;
  const me = useAuth().user?.username;
  const [role, setRole] = useState<AppWorkspaceAddRole>(suggestedRole);
  const [typed, setTyped] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<AddedPerson[]>([]);
  // Who has been added, by the username the person chose (the app's answer
  // may carry only a ref).
  const [addedUsernames, setAddedUsernames] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const coMembers = (members.data ?? []).filter(
    (m) =>
      m.status === 'active' &&
      !!m.username &&
      m.username !== me &&
      !addedUsernames.includes(m.username),
  );

  function toggle(username: string) {
    setChosen((c) => (c.includes(username) ? c.filter((u) => u !== username) : [...c, username]));
  }

  function addTyped() {
    const username = typed.trim().replace(/^@/, '');
    if (username && !chosen.includes(username)) setChosen((c) => [...c, username]);
    setTyped('');
  }

  async function addChosen() {
    setBusy(true);
    const failed: Record<string, string> = {};
    const nowAdded: AddedPerson[] = [];
    const nowAddedUsernames: string[] = [];
    for (const username of chosen) {
      try {
        const { member } = await api.addPersonToAppWorkspace(archeId, {
          workspaceId,
          username,
          role,
        });
        // A member the server could not describe after the add committed is
        // still added; the app sees them in its next listMembers.
        if (member.ref) {
          nowAdded.push({
            ref: member.ref,
            displayName: member.displayName ?? username,
            role: member.role,
          });
        }
        nowAddedUsernames.push(username);
      } catch (err) {
        failed[username] = describeAddError(err);
      }
    }
    const all = [...added, ...nowAdded];
    setAdded(all);
    setAddedUsernames((u) => [...u, ...nowAddedUsernames]);
    setErrors(failed);
    setChosen(Object.keys(failed));
    setBusy(false);
    // Everyone was added: the sheet's job is done.
    if (Object.keys(failed).length === 0) onResolve({ added: all });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onResolve({ added })}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>Add people to {archeName}</DialogTitle>
          <DialogDescription>
            Shown by Fias.{' '}
            {workspaceName ? (
              <>
                {archeName} asked to add people to its workspace “{workspaceName}”.{' '}
              </>
            ) : null}
            The people you add can use this workspace’s data in {archeName} with the role you
            choose. {archeName} learns only who you added.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="add-people-role">Role</Label>
            <select
              id="add-people-role"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as AppWorkspaceAddRole)}
              disabled={busy}
            >
              {(Object.keys(ROLE_LABELS) as AppWorkspaceAddRole[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>

          {holderId && coMembers.length > 0 && (
            <fieldset className="space-y-1">
              <legend className="text-sm font-medium">People in this workspace</legend>
              {coMembers.map((m) => (
                <label key={m.membershipId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={chosen.includes(m.username!)}
                    onChange={() => toggle(m.username!)}
                    disabled={busy}
                  />
                  {m.displayName ?? m.username}{' '}
                  <span className="text-muted-foreground">@{m.username}</span>
                </label>
              ))}
            </fieldset>
          )}

          <div className="space-y-1">
            <Label htmlFor="add-people-username">Someone else, by their exact username</Label>
            <div className="flex gap-2">
              <Input
                id="add-people-username"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTyped();
                  }
                }}
                placeholder="username"
                autoComplete="off"
                disabled={busy}
              />
              <Button type="button" variant="outline" onClick={addTyped} disabled={busy}>
                Include
              </Button>
            </div>
          </div>

          {chosen.length > 0 && (
            <ul className="space-y-1 text-sm" aria-label="To add">
              {chosen.map((u) => (
                <li key={u} className="flex items-center justify-between gap-2">
                  <span>
                    @{u}
                    {errors[u] && <span className="ml-2 text-destructive">{errors[u]}</span>}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggle(u)}
                    disabled={busy}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {added.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Added: {addedUsernames.map((u) => `@${u}`).join(', ')}
            </p>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onResolve({ added })} disabled={busy}>
            {added.length > 0 ? 'Done' : 'Cancel'}
          </Button>
          <Button onClick={() => void addChosen()} disabled={busy || chosen.length === 0}>
            {busy ? 'Adding…' : `Add ${chosen.length || ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
